import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Cookie, Settings } from 'lucide-react';
import Disclaimer from '../components/Disclaimer';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Cookies = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Disclaimer />
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" data-discover="true" className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour à l'accueil
        </Link>

        <article className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Cookie className="w-6 h-6 text-purple-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
              Politique des Cookies
            </h1>
          </div>
          
          <div className="prose prose-lg max-w-none text-gray-700 space-y-6 leading-relaxed">
            <p className="text-sm text-gray-500 mb-8">
              Dernière mise à jour : 8 décembre 2025
            </p>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Qu'est-ce qu'un Cookie ?</h2>
              <p>
                Un cookie est un petit fichier texte stocké sur votre appareil (ordinateur, tablette, smartphone) lorsque vous visitez un 
                site web. Les cookies permettent au site de reconnaître votre appareil et de mémoriser certaines informations sur vos 
                préférences ou actions passées.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Comment Nous Utilisons les Cookies</h2>
              <p>
                Nous utilisons des cookies pour améliorer votre expérience sur notre site, analyser l'utilisation du site, et personnaliser 
                le contenu. Les cookies nous aident à comprendre comment vous utilisez le site et à améliorer nos services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Settings className="w-6 h-6 text-purple-600" />
                3. Types de Cookies que Nous Utilisons
              </h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">3.1. Cookies Strictement Nécessaires</h3>
              <p>
                Ces cookies sont essentiels au fonctionnement du site et ne peuvent pas être désactivés. Ils sont généralement définis en 
                réponse à des actions que vous effectuez et qui équivalent à une demande de services, comme définir vos préférences de 
                confidentialité, vous connecter ou remplir des formulaires.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">3.2. Cookies de Performance</h3>
              <p>
                Ces cookies nous permettent de compter les visites et les sources de trafic afin d'améliorer les performances de notre site. 
                Ils nous aident à savoir quelles pages sont les plus et les moins populaires et à voir comment les visiteurs se déplacent sur 
                le site. Toutes les informations collectées par ces cookies sont agrégées et donc anonymes.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">3.3. Cookies de Fonctionnalité</h3>
              <p>
                Ces cookies permettent au site de fournir des fonctionnalités et une personnalisation améliorées. Ils peuvent être définis par 
                nous ou par des fournisseurs tiers dont nous avons ajouté les services à nos pages.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">3.4. Cookies de Ciblage</h3>
              <p>
                Ces cookies peuvent être définis sur notre site par nos partenaires publicitaires. Ils peuvent être utilisés par ces entreprises 
                pour créer un profil de vos intérêts et vous montrer des publicités pertinentes sur d'autres sites. Ils ne stockent pas directement 
                d'informations personnelles, mais sont basés sur l'identification unique de votre navigateur et de votre appareil Internet.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Cookies Tiers</h2>
              <p>
                Certains cookies sont placés par des services tiers qui apparaissent sur nos pages. Nous n'avons pas le contrôle sur ces cookies 
                tiers. Nous vous encourageons à consulter les politiques de cookies de ces services tiers pour comprendre comment ils utilisent 
                les cookies.
              </p>
              <p>
                Les services tiers que nous utilisons peuvent inclure des outils d'analyse (comme Google Analytics), des réseaux publicitaires, 
                et d'autres services qui nous aident à améliorer notre site et à comprendre notre audience.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Durée de Conservation des Cookies</h2>
              <p>
                Les cookies que nous utilisons ont différentes durées de vie :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Cookies de session</strong> : Supprimés automatiquement lorsque vous fermez votre navigateur</li>
                <li><strong>Cookies persistants</strong> : Restent sur votre appareil pendant une période déterminée ou jusqu'à ce que vous les 
                supprimiez manuellement</li>
              </ul>
              <p className="mt-4">
                La durée de conservation spécifique de chaque cookie peut varier. La plupart de nos cookies persistent entre 30 jours et 2 ans.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Gestion des Cookies</h2>
              <p>
                Vous avez le contrôle sur les cookies. La plupart des navigateurs acceptent automatiquement les cookies, mais vous pouvez modifier 
                les paramètres de votre navigateur pour refuser les cookies si vous le souhaitez.
              </p>
              <p>
                Voici comment gérer les cookies dans les principaux navigateurs :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Chrome</strong> : Paramètres → Confidentialité et sécurité → Cookies et autres données de sites</li>
                <li><strong>Firefox</strong> : Options → Vie privée et sécurité → Cookies et données de sites</li>
                <li><strong>Safari</strong> : Préférences → Confidentialité → Cookies et données de sites web</li>
                <li><strong>Edge</strong> : Paramètres → Cookies et autorisations de site → Cookies et données de sites</li>
              </ul>
              <p className="mt-4">
                Veuillez noter que la désactivation de certains cookies peut affecter le fonctionnement du site et limiter votre expérience 
                utilisateur.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Consentement aux Cookies</h2>
              <p>
                En continuant à utiliser notre site après avoir été informé de notre utilisation des cookies, vous acceptez que nous utilisions 
                des cookies conformément à cette politique. Vous pouvez retirer votre consentement à tout moment en modifiant les paramètres de 
                votre navigateur ou en nous contactant.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Cookies et Données Personnelles</h2>
              <p>
                Certains cookies peuvent collecter des informations qui constituent des données personnelles. Pour plus d'informations sur la 
                façon dont nous traitons vos données personnelles, consultez notre <Link to="/privacy-policy" data-discover="true" className="text-purple-600 hover:text-purple-700 underline">Politique de Confidentialité</Link>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Modifications de Cette Politique</h2>
              <p>
                Nous pouvons modifier cette politique des cookies occasionnellement pour refléter les changements dans notre utilisation des 
                cookies ou pour d'autres raisons opérationnelles, légales ou réglementaires. Les modifications seront publiées sur cette page 
                avec une date de mise à jour révisée.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Contact</h2>
              <p>
                Pour toute question concernant notre utilisation des cookies, contactez-nous :
              </p>
              <div className="bg-gray-50 p-6 rounded-lg mt-4">
                <p><strong>Email :</strong> <a href="mailto:info@lyonpicks.com" className="text-purple-600 hover:text-purple-700 underline">info@lyonpicks.com</a></p>
                <p><strong>Téléphone :</strong> <a href="tel:+33289858716" className="text-purple-600 hover:text-purple-700 underline">+33 2 89 85 87 16</a></p>
                <p><strong>Adresse :</strong> Paris, France</p>
              </div>
            </section>
          </div>
        </article>
      </div>

      <Footer />
    </div>
  );
};

export default Cookies;

